import React, { useCallback } from "react";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import makeStyles from "@mui/styles/makeStyles";

import { CustomStatusDropdownV2 } from "components/CustomDropdown/CustomStatusDropdownV2";
import { SeverityChips } from "components/CaseManagement/CaseDetail/SeverityChips";

import { UNKNOWN } from "core/constants";
import { useCaseStatusEnums } from "hooks/useCaseStatusEnums";

import styles from "assets/jss/default/components/caseStatusTableStyle.js";

const useStyles = makeStyles(styles);

export function CaseStatusFields(props) {
  const { caseModel, onChange, scalarConfigs } = props;

  const { error, loading, data } = useCaseStatusEnums(scalarConfigs);

  const classes = useStyles();

  const handleFieldChange = useCallback(
    (fieldName, value) => {
      const newModel = { ...caseModel, [fieldName]: value };
      onChange({ isValid: true, model: newModel });
    },
    [caseModel, onChange]
  );

  if (!loading && (!data || error))
    return <div>There was an error loading the data.</div>;

  const caseStatusName = data.caseStatusTypeValues?.find(
    ({ id }) => id === caseModel.status
  )?.name;

  const componentName = data.componentValues?.find(
    ({ id }) => id === caseModel.component
  )?.name;

  const priorityType = data.casePriorityTypeValues?.find(
    ({ id }) => id === caseModel.priority
  );

  const displaySkeletons =
    loading ||
    !data.componentValues ||
    !data.caseStatusTypeValues ||
    !data.casePriorityTypeValues;

  return (
    <div className={classes.caseStatusTableWrapper}>
      <Grid direction="row" alignItems="flex-start" container>
        <Grid item xs={12} md={12}>
          <Grid container direction="row" alignItems="center">
            {displaySkeletons ? (
              <CaseStatusFieldsSkeleton />
            ) : (
              <>
                <Grid item xs={12} md={3}>
                  <CustomStatusDropdownV2
                    dataQa="CP-Button-Case-Status"
                    headerText="Case Status"
                    buttonText={caseStatusName ?? UNKNOWN}
                    dropdownList={data.caseStatusTypeValues}
                    onMenuItemClicked={({ id }) =>
                      handleFieldChange("status", id)
                    }
                    buttonProps={{
                      style: {
                        backgroundColor: "#4A4A4A",
                        color: "#FFFFFF"
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <CustomStatusDropdownV2
                    dataQa="CP-Button-Component"
                    headerText="Component"
                    buttonText={componentName ?? UNKNOWN}
                    dropdownList={data.componentValues}
                    onMenuItemClicked={({ id }) =>
                      handleFieldChange("component", id)
                    }
                    buttonProps={{
                      style: {
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        color: "#FFFFFF"
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <CustomStatusDropdownV2
                    dataQa="CP-Button-Priority"
                    headerText="Priority"
                    buttonText={priorityType?.name ?? UNKNOWN}
                    dropdownList={data.casePriorityTypeValues}
                    onMenuItemClicked={({ id }) =>
                      handleFieldChange("priority", id)
                    }
                    buttonProps={{
                      style: {
                        backgroundColor:
                          priorityType?.colorObject.backgroundColor[0],
                        color: priorityType?.colorObject.color
                      }
                    }}
                  />
                </Grid>
              </>
            )}
            <SeverityChips />
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

function CaseStatusFieldsSkeleton() {
  const classes = useStyles();

  return (
    <>
      <Grid item xs={12} md={3}>
        <div className={classes.caseStatusWrapper}>
          <Skeleton variant="text" />
          <Skeleton
            variant="rectangular"
            width={74}
            height={29}
            animation="wave"
          />
        </div>
      </Grid>
      <Grid item xs={12} md={3}>
        <div className={classes.caseStatusWrapper}>
          <Skeleton variant="text" />
          <Skeleton
            variant="rectangular"
            width={74}
            height={29}
            animation="wave"
          />
        </div>
      </Grid>
      <Grid item xs={12} md={2}>
        <div className={classes.caseStatusWrapper}>
          <Skeleton variant="text" />
          <Skeleton
            variant="rectangular"
            width={74}
            height={29}
            animation="wave"
          />
        </div>
      </Grid>
    </>
  );
}

CaseStatusFields.propTypes = {
  caseModel: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  scalarConfigs: PropTypes.array
};

CaseStatusFields.defaultProps = {
  caseModel: null
};
